// src/components/forms/RecruitPlanForm.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Zoom,
  styled,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  CircularProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { motion } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { designTokens } from '../../theme/designTokens';
import { bg, text as textColors, border, shadow, overlay, statusBg, adaptiveColor } from '../../utils/darkModeAdapter';
import { saveCommonFields, loadCommonFields } from '../../utils/commonProfileStore';

// モックのアドバイスデータ（各項目は { advice, reason } 形式）
const localAdviceMock = {
  startDate: {
    advice: "【モックAIアドバイス】採用開始日はできるだけ早めに設定してください。",
    reason: "早期開始により応募者が増えるためです。"
  },
  endDate: {
    advice: "【モックAIアドバイス】採用終了日は十分な余裕を持って設定してください。",
    reason: "余裕を持つことで調整がしやすくなります。"
  },
  targetHires: {
    advice: "【モックAIアドバイス】希望人数は会社規模に応じた現実的な数字にしてください。",
    reason: "過大な目標は応募者の質を下げる可能性があります。"
  },
  budget: {
    advice: "【モックAIアドバイス】予算配分は各媒体とのバランスを考慮しましょう。",
    reason: "適切なバランスが採用効果に直結します。"
  },
  position: {
    advice: "【モックAIアドバイス】募集職種は具体的に記載しましょう。",
    reason: "明確な職種提示が適切な応募者の獲得に繋がります。"
  },
  desiredSkillLevel: {
    advice: "【モックAIアドバイス】求めるスキルレベルを明確にしてください。",
    reason: "具体的なスキル要件は応募者の適性を向上させます。"
  },
  orgIssues: {
    advice: "【モックAIアドバイス】組織の課題を具体的に明示しましょう。",
    reason: "内部の課題が採用戦略の基盤となるためです。"
  }
  // 他のオプション項目も必要に応じて追加可能
};

// フロントエンドでのモック使用フラグ（false にすると API 呼び出しになります）
const USE_MOCK = false;
const API_BASE_URL = process.env.REACT_APP_API_URL;
// 新卒/中途のチェックボックス用初期状態
const initialRecruitTypeState = {
  newGradChecked: false,
  midCareerChecked: false,
};

// スタイルされたコンポーネント
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: 12,
  backgroundColor: bg(theme).card,
  boxShadow: designTokens.shadows.sm,
  border: `1px solid ${border(theme).light}`,
  width: '100%',
  maxWidth: 960,
  position: 'relative',
  overflow: 'hidden',
  transition: designTokens.transitions.normal,
  '&:hover': {
    boxShadow: designTokens.shadows.md,
  },
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  fontWeight: 700,
  textAlign: 'center',
  color: theme.palette.text.primary,
  fontSize: '1.8rem',
  letterSpacing: '-0.02em',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    transition: 'all 0.2s ease',
    backgroundColor: bg(theme).card,
    '&:hover': {
      boxShadow: designTokens.shadows.sm,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${designTokens.colors.primary}22`,
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: designTokens.colors.primary,
    }
  },
  '& .MuiFormHelperText-root': {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }
}));

const BudgetChip = styled('span')(({ theme }) => ({
  backgroundColor: statusBg(theme).info,
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  fontSize: '0.85rem',
  padding: '4px 12px',
  borderRadius: '16px',
  display: 'inline-block',
  lineHeight: '1.2',
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.8, 5),
  borderRadius: 8,
  fontSize: '1.1rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  backgroundColor: designTokens.colors.primary,
  boxShadow: designTokens.shadows.sm,
  transition: 'all 0.2s ease',
  textTransform: 'none',
  position: 'relative',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: designTokens.shadows.md,
  },
  '&:active': {
    boxShadow: designTokens.shadows.sm,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
    boxShadow: 'none',
  },
  // MUIのリップルエフェクトを強化
  '& .MuiTouchRipple-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  }
}));

const CheckboxGroup = styled(FormGroup)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2, 3),
  borderRadius: 8,
  backgroundColor: bg(theme).subtle,
  border: `1px solid ${border(theme).light}`,
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: 8,
  backgroundColor: bg(theme).subtle,
  boxShadow: 'none',
  border: `1px solid ${border(theme).light}`,
  '&::before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    borderRadius: 8,
    '&:hover': {
      backgroundColor: bg(theme).tinted,
    }
  }
}));

// カスタム Tooltip: 吹き出しを右側に固定し、大きめサイズに設定
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    TransitionComponent={Zoom}
    arrow
    classes={{ popper: className }}
    PopperProps={{
      modifiers: [
        { name: 'flip', enabled: false },
        { name: 'preventOverflow', enabled: false },
      ],
    }}
    placement="top-end"
  />
))(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: designTokens.colors.info,
    color: '#fff',
    fontWeight: 600,
    boxShadow: designTokens.shadows.md,
    borderRadius: 8,
    padding: '16px 20px',
    minWidth: '220px',
    fontSize: '1.0rem',
  },
  '& .MuiTooltip-arrow': {
    color: designTokens.colors.info,
  },
}));

// AI アドバイスアイコンコンポーネント
function AdviceIcon({ fieldName, adviceMap }) {
  let adviceValue = adviceMap[fieldName];
  let adviceText = "";
  if (adviceValue && typeof adviceValue === "object") {
    // adviceValue は { advice, reason } 形式のオブジェクトなので、文字列に変換
    adviceText = `${adviceValue.advice} ${adviceValue.reason}`;
  } else if (typeof adviceValue === "string") {
    adviceText = adviceValue;
  } else {
    adviceText = "アドバイスは後日取得";
  }

  return (
    <StyledTooltip title={adviceText} enterDelay={200}>
      <IconButton
        size="small"
        component={motion.button}
        whileHover={{ scale: 1.25, rotate: [0, 10, -10, 0] }}
        whileTap={{ scale: 0.9 }}
        sx={{
          p: 0,
          m: 0,
          color: designTokens.colors.warning,
          transition: 'color 0.2s',
          '&:hover': { color: '#ff6f00' },
        }}
      >
        <TipsAndUpdatesIcon
          fontSize="small"
          sx={{
            backgroundColor: designTokens.colors.secondary,
            borderRadius: '50%',
            padding: '2px',
          }}
        />
      </IconButton>
    </StyledTooltip>
  );
}

// RecruitPlanForm コンポーネント
export default function RecruitPlanForm({ defaultValues, onSubmit, onBack }) {
  const theme = useTheme();
  const [formData, setFormData] = useState(() => {
    const saved = loadCommonFields();
    return {
      ...defaultValues,
      position: defaultValues.position || saved.position || '',
      budget: defaultValues.budget || saved.salary || '',
    };
  });
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [adviceMap, setAdviceMap] = useState({});
  const [isAdviceLoaded, setIsAdviceLoaded] = useState(false);
  const [recruitTypeState, setRecruitTypeState] = useState(initialRecruitTypeState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // マウント時にアドバイスを取得
  useEffect(() => {
    if (USE_MOCK) {
      // すぐモックアドバイスを設定してロード完了をtrue
      setAdviceMap(localAdviceMock);
      setIsAdviceLoaded(true);
    } else {
      axios
        .get(`${API_BASE_URL}/recruit_plan_assistant/company_profile_advice`, { withCredentials: true })
        .then((res) => {
          if (res.data && res.data.adviceMap) {
            setAdviceMap(res.data.adviceMap);
          } else {
            setAdviceMap(localAdviceMock);
          }
        })
        .catch((err) => {
          console.error('Advice API error:', err);
          setAdviceMap(localAdviceMock);
        })
        .finally(() => {
          // 取得成功/失敗問わず、ロード完了したのでtrueにする
          setIsAdviceLoaded(true);
        });
    }
  }, []);

  // フォームコンポーネント内で定義
  const handleRecruitTypeChange = (e) => {
    const { name, checked } = e.target;
    setRecruitTypeState((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  // 入力が空の場合、背景色を変える
  const getRequiredFieldStyle = (value) => {
    // もし value が undefined/null なら空文字として扱う
    const safeValue = value ?? '';
      return {
        backgroundColor: safeValue.trim() === '' ? adaptiveColor(theme, 'rgba(255,248,248,0.8)', 'rgba(60,30,30,0.8)') : adaptiveColor(theme, 'rgba(255,255,255,0.9)', 'rgba(30,30,30,0.9)'),
        borderRadius: '12px',
      };
  };

  // フォームの入力変更時のハンドラ
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;

    // 希望人数、予算などは数字のみ許可 (type="number")
    if (name === 'targetHires' || name === 'budget') {
      // 半角数字のみ残す（例: "abc123" -> "123"）
      newValue = newValue.replace(/[^0-9]/g, '');
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  // 金額の表示形式を変換（例：500000 → 50万円）
  const formatBudget = (value) => {
    if (!value) return '';
    const num = parseInt(value);
    if (isNaN(num) || num === 0) return '';
    if (num < 10000) return `${num.toLocaleString()}円`;
    return `${Math.floor(num / 10000)}万円`;
  };

  // バリデーションチェック用の関数
  const validateForm = () => {
    // 必須項目
    const requiredFields = [
      'startDate',
      'endDate',
      'targetHires',
      'budget',
      'position',
      'desiredSkillLevel',
      'orgIssues',
    ];

    // (1) 必須項目が埋まっているか
    for (let field of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        return false;
      }
    }

    // (2) 日付チェック: endDate >= startDate
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end < start) {
        return false;
      }
    }

    // (3) 希望人数・予算が数字かつ1以上などの制限 (必要なら)
    //   → 下記は簡易的に "数字として解釈できればOK" のチェック例
    if (Number(formData.targetHires) <= 0) {
      return false;
    }
    if (Number(formData.budget) <= 0) {
      return false;
    }

    // (4) 新卒/中途のいずれかが選択されているか
    if (!recruitTypeState.newGradChecked && !recruitTypeState.midCareerChecked) {
      return false;
    }

    return true;
  };

  // フォーム送信時
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert("フォームの入力に不備があります。");
      return;
    }

    setIsSubmitting(true);
    try {
      // recruitType を計算
      let recruitType = "";
      if (recruitTypeState.newGradChecked && recruitTypeState.midCareerChecked) {
        recruitType = "both";
      } else if (recruitTypeState.newGradChecked) {
        recruitType = "newGrad";
      } else {
        recruitType = "midCareer";
      }

      // バックエンドが期待する「requirements」キーで包んだデータ
      const finalPayload = {
        requirements: {
          ...formData,         // startDate, endDate, ... など全て含む
          recruitType: recruitType
        }
      };

      // 共通プロフィールに保存
      saveCommonFields({ position: formData.position, salary: formData.budget });

      // 親から受け取った onSubmit に、この形で渡す
      await onSubmit(finalPayload);
    } catch (error) {
      console.error('Submit error:', error);
      alert('送信中にエラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  // フォームUI描画
  if (!isAdviceLoaded) {
    // アドバイスが未ロードなら「ロード中...」などを表示する
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
          情報を読み込んでいます...
        </Typography>
      </Box>
    );
  }

  // フォームUI
  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '70vh',
        padding: theme => theme.spacing(4, 2),
      }}
    >
      <StyledPaper elevation={3}>
        <Box sx={{ position: 'relative', mb: 2 }}>
          {onBack && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              size="small"
              sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary', minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              戻る
            </Button>
          )}
          <FormTitle variant="h5" sx={{ mb: 0, textAlign: 'center' }}>
            採用計画の基本情報を入力
          </FormTitle>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: textColors(theme).secondary }}>
            【必須入力項目】
          </Typography>

          {/* 新卒 or 中途 チェックボックス */}
          <CheckboxGroup row>
            {/* 新卒採用 */}
            <FormControlLabel
              control={
                <Checkbox
                  icon={<CheckCircleOutlineIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  checked={recruitTypeState.newGradChecked}
                  onChange={handleRecruitTypeChange}
                  name="newGradChecked"
                  sx={{
                    color: '#1E88E5',
                    '&.Mui-checked': {
                      color: '#1E88E5',
                    },
                  }}
                />
              }
              label="新卒採用"
              sx={{
                '.MuiFormControlLabel-label': {
                  fontWeight: 'bold',
                  color: '#0D47A1',
                },
              }}
            />

            {/* 中途採用 */}
            <FormControlLabel
              control={
                <Checkbox
                  icon={<CheckCircleOutlineIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  checked={recruitTypeState.midCareerChecked}
                  onChange={handleRecruitTypeChange}
                  name="midCareerChecked"
                  sx={{
                    color: '#1E88E5',
                    '&.Mui-checked': {
                      color: '#1E88E5',
                    },
                  }}
                />
              }
              label="中途採用"
              sx={{
                '.MuiFormControlLabel-label': {
                  fontWeight: 'bold',
                  color: '#0D47A1',
                },
              }}
            />
          </CheckboxGroup>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, mb: 2, display: 'block' }}>
            ※ 新卒・中途いずれか、あるいは両方を選択してください
          </Typography>

          {/* 採用開始日 */}
          <StyledTextField
            label="採用開始日 *"
            name="startDate"
            type="date"
            required
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            value={formData.startDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              style: getRequiredFieldStyle(formData.startDate),
              endAdornment: <AdviceIcon fieldName="startDate" adviceMap={adviceMap} />,
            }}
            helperText="採用活動を開始したい日付（応募受付開始日）を入力してください。例：2025-06-01"
            sx={{ mt: 1 }}
          />

          {/* 採用終了日 */}
          <StyledTextField
            label="採用終了日 *"
            name="endDate"
            type="date"
            required
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            value={formData.endDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              style: getRequiredFieldStyle(formData.endDate),
              endAdornment: <AdviceIcon fieldName="endDate" adviceMap={adviceMap} />,
            }}
            helperText="採用活動を終了したい日付（応募受付終了日）を入力してください。開始日以降の日付を指定してください。"
            sx={{ mt: 1 }}
          />

          {/* 希望人数 */}
          <StyledTextField
            label="希望人数 *"
            name="targetHires"
            type="number"
            required
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            value={formData.targetHires}
            onChange={handleChange}
            InputProps={{
              style: getRequiredFieldStyle(formData.targetHires),
              endAdornment: <AdviceIcon fieldName="targetHires" adviceMap={adviceMap} />,
              inputProps: { min: 1 }, // 1以上のみ
            }}
            helperText="今回の採用で確保したい人数を入力してください。例：5名採用したい場合は「5」と入力"
            sx={{ mt: 1 }}
          />

          {/* 採用予算 */}
          <StyledTextField
            label="採用予算（円）*"
            name="budget"
            type="number"
            required
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            value={formData.budget}
            onChange={handleChange}
            InputProps={{
              style: getRequiredFieldStyle(formData.budget),
              endAdornment: <AdviceIcon fieldName="budget" adviceMap={adviceMap} />,
              inputProps: { min: 1 }, // 1以上のみ
            }}
            helperText={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ color: textColors(theme).muted }}>
                  上記で入力された人数を採用する際の1ヶ月あたりの予算を入力してください。例：採用期間2ヶ月　採用人数３名　一人当たり月5万円までの予算の場合『300000』
                </span>
                {formData.budget && (
                  <BudgetChip>
                    {formatBudget(formData.budget)}
                  </BudgetChip>
                )}
              </span>
            }
            sx={{ mt: 1 }}
          />

          {/* 募集職種 */}
          <StyledTextField
            label="募集職種 *"
            name="position"
            required
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            value={formData.position}
            onChange={handleChange}
            InputProps={{
              style: getRequiredFieldStyle(formData.position),
              endAdornment: <AdviceIcon fieldName="position" adviceMap={adviceMap} />,
            }}
            sx={{ mt: 1 }}
            helperText="募集したい職種・ポジション名を入力してください。例：営業職、エンジニア、マーケティング担当など"
          />

          {/* 想定スキルレベル */}
          <StyledTextField
            label="想定スキルレベル・求める人物像 *"
            name="desiredSkillLevel"
            required
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            value={formData.desiredSkillLevel}
            onChange={handleChange}
            InputProps={{
              style: getRequiredFieldStyle(formData.desiredSkillLevel),
              endAdornment: <AdviceIcon fieldName="desiredSkillLevel" adviceMap={adviceMap} />,
            }}
            sx={{ mt: 1 }}
            helperText="求める経験・スキル・人物像を具体的に記載してください。例：3年以上のJava開発経験、チームリーダー経験、コミュニケーション能力が高い方など"
          />

          {/* 組織の課題・採用背景 */}
          <StyledTextField
            label="組織の課題・採用背景 *"
            name="orgIssues"
            required
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            size="small"
            margin="dense"
            value={formData.orgIssues}
            onChange={handleChange}
            InputProps={{
              style: getRequiredFieldStyle(formData.orgIssues),
              endAdornment: (
                <Box sx={{ alignSelf: 'flex-start' }}>
                  <AdviceIcon fieldName="orgIssues" adviceMap={adviceMap} />
                </Box>
              ),
            }}
            sx={{ mt: 1, mb: 3 }}
            helperText="なぜ今回採用が必要なのか、組織の課題や背景を記載してください。例：事業拡大に伴う増員、退職者の補充、新規プロジェクト立ち上げなど"
          />

          {/* 任意入力項目 */}
          <StyledAccordion
            expanded={optionalOpen}
            onChange={() => setOptionalOpen(!optionalOpen)}
            sx={{ mb: 3 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {optionalOpen ? '任意入力項目（クリックで閉じる）' : '任意入力項目（クリックで開く）'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* 在籍人材のスキル傾向 */}
              <StyledTextField
                label="在籍人材のスキル傾向"
                name="existingTalentSkill"
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                value={formData.existingTalentSkill}
                onChange={handleChange}
                sx={{ mb: 2 }}
                helperText="現在社内にいる人材のスキル分布や特徴を記載してください。例：エンジニアが多い、営業経験者が少ないなど"
              />
              {/* 過去の採用実績 */}
              <StyledTextField
                label="過去の採用実績"
                name="pastRecruitData"
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                size="small"
                margin="dense"
                value={formData.pastRecruitData}
                onChange={handleChange}
                sx={{ mb: 2 }}
                helperText="過去の採用での成功事例や失敗事例、採用人数などを記載してください。例：昨年度は5名採用、定着率80%など"
              />
              {/* 雇用形態 */}
              <StyledTextField
                label="雇用形態"
                name="employmentType"
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                value={formData.employmentType}
                onChange={handleChange}
                sx={{ mb: 2 }}
                helperText="正社員、契約社員、パート・アルバイトなど、希望する雇用形態を記載してください"
              />
              {/* 勤務形態 */}
              <StyledTextField
                label="働き方"
                name="workStyle"
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                value={formData.workStyle}
                onChange={handleChange}
                sx={{ mb: 2 }}
                helperText="フルリモート、ハイブリッド、出社必須など、勤務形態を記載してください"
              />
              {/* 優先順位・スケジュール感 */}
              <StyledTextField
                label="優先順位・スケジュール感"
                name="prioritySchedule"
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                size="small"
                margin="dense"
                value={formData.prioritySchedule}
                onChange={handleChange}
                sx={{ mb: 2 }}
                helperText="採用の優先度や入社希望時期などを記載してください。例：4月入社必須、できるだけ早期に採用したいなど"
              />
            </AccordionDetails>
          </StyledAccordion>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <SubmitButton
              type="submit"
              variant="contained"
              size="large"
              disabled={!validateForm() || isSubmitting}
              component={motion.button}
              whileHover={{ scale: (!validateForm() || isSubmitting) ? 1 : 1.02 }}
              whileTap={{ scale: (!validateForm() || isSubmitting) ? 1 : 0.98 }}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSubmitting ? '送信中...' : '送信'}
            </SubmitButton>
          </Box>
        </Box>
      </StyledPaper>
    </Box>
  );
}